import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { db, FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
import { useDispatch, useSelector } from 'react-redux'
import { OptionType, ScheduleType } from 'types/schedule'
import { State } from 'index'
import { getIsAdmin, getStoreRef } from 'reducks/auth/selectors'
import { confirmSchedules, fetchSchedulesOfBread } from 'reducks/schedule/operations'
import { Caution, MainHeadline, SubDescription } from 'components/Typography'
import { useImageUrls, useSelect, useString, useWysiwygEditor } from 'hooks/form'
import { useNumberChangeEvent } from 'hooks/form'
import { Spacer } from 'components/Utility'
import {
  ProductEditAllergensSection,
  ProductEditBaseSection,
  ProductEditDailySchedule,
  ProductEditDelivery,
  ProductEditPreorderSchedule,
} from 'components/Product'
import { getCategories } from 'reducks/category/selectors'
import { fetchEnabledCategories } from 'reducks/category/operations'
import { FormLabel } from 'components/Label'
import { getSchedules } from 'reducks/schedule/selectors'
import { ProductSizeForm, RadioButtonsForm, TagCheckbox, TextInput } from 'components/Form'
import { BlueButton, NavButton } from 'components/Button'
import { getShopRef } from 'reducks/store/selectors'
import { confirmBread, deleteBread } from 'reducks/bread/operations'
import { push } from 'connected-react-router'
import { getBread } from 'reducks/bread/selectors'
import { fetchFutureStocks } from 'reducks/stock/operations'
import { StockType, StockUnionType } from 'types/stock'
import { getStocks } from 'reducks/stock/selectors'
import { openCautionModal } from 'reducks/modal/operations'
import { useFilledRequiredForm } from 'hooks/product'
import { useSelectAllDaysOfWeek, useUnselectAllDaysOfWeek } from 'hooks/schedule'
import { ProductEditError } from 'components/Error'
import {
  useAddDefaultStock,
  useAddSchedule,
  useDeleteDefaultStock,
  useDeleteSchedule,
  useHandleAllergens,
  useHandleDefaultStock,
  useHandleHoursToDeadline,
  useHandleScheduledGeneration,
  useHandleStatus,
  useInitSchedulesByType,
  useSelectDaysOfWeek,
  useSelectMaximumOrder,
  useToggleScheduleType,
  useToggleTypeDaily,
  useUpdateNote,
} from 'hooks/bread'
import { useStoreDeliveryBread } from 'hooks/storeDeliveryBread'
import { StoreDeliveryBreadType } from 'types/storeDeliveryBread'
import { getStoreDeliveryBread } from 'reducks/storeDeliveryBread/selectors'
import { fetchStoreDeliveryBread } from 'reducks/storeDeliveryBread/operations'
import { confirmStoreDeliveryBread } from 'reducks/storeDeliveryBread/operations'
import { useFetchLabels, useHandleBreadLabels } from 'hooks/label'
import { FlexRow } from 'components/Flex'
import confirmBreadLabels from 'reducks/bread/operations/confrimBreadLabels'
import { PackageSize } from 'types/storeDeliveryFee'
import { useFetchStoreDefault } from 'hooks/storeDefault'

export const getUpdateSchedulesFunction = (
  type: StockUnionType | '',
  setDailySchedules: Dispatch<SetStateAction<ScheduleType[]>>,
  setPreorderSchedules: Dispatch<SetStateAction<ScheduleType[]>>
): Dispatch<SetStateAction<ScheduleType[]>> | undefined => {
  switch (type) {
    case 'daily':
      return setDailySchedules
    case 'preorder':
      return setPreorderSchedules
    default:
      return undefined
  }
}

const statuses = [
  { label: '出品中', value: 'public' },
  { label: '出品停止', value: 'private' },
]

type Props = {
  isEditPage: boolean
}

const ProductEditTemplate: FC<Props> = (props) => {
  const { isEditPage } = props

  const dispatch = useDispatch()

  const bread = useSelector((state: State) => getBread(state))
  const categories = useSelector((state: State) => getCategories(state))
  const isAdmin = useSelector((state: State) => getIsAdmin(state))
  const shopRef = useSelector((state: State) => getShopRef(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const schedules: ScheduleType[] = useSelector((state: State) => getSchedules(state))
  const stocks: StockType[] = useSelector((state: State) => getStocks(state))
  const storeDeliveryBread: StoreDeliveryBreadType = useSelector((state: State) => getStoreDeliveryBread(state))
  const labels = useFetchLabels()

  const { allergens, isSetAllergens, handleAllergens, handleSetAllergens, setAllergens, setIsSetAllergens } =
    useHandleAllergens()
  const [breadRef, setBreadRef] = useState<FirebaseDocRefType>(db.collection('breads').doc())
  const { state: name, handleState: handleName, setState: setName } = useString<string>('')
  const { state: shortName, handleState: handleShortName, setState: setShortName } = useString<string>('')
  const {
    currentLength: currentDescriptionLength,
    editorState: description,
    initEditorState: setDescription,
    onEditorStateChange: handleDescription,
    parseEditorStateToHtml: parseDescriptionToHtml,
    replaceImage,
  } = useWysiwygEditor()
  const { imageUrls, handleImageUrls, deleteImageUrls, rearrangeImages, setImageUrls } = useImageUrls()
  const { state: categoryId, handleState: selectCategoryName, setState: setCategoryId } = useSelect<string>('')
  const {
    state: expirationDate,
    handleState: handleExpirationDate,
    setState: setExpirationDate,
  } = useString<string>('')
  const { breadLabels, handleBreadLabels } = useHandleBreadLabels(bread, labels)
  const [price, setPrice] = useState<number>(0)
  const [status, setStatus] = useState<string>('public')
  const [isScheduledGeneration, setScheduledGeneration] = useState<boolean>(false)
  const [isTypeDaily, setIsTypeDaily] = useState<boolean>(false)
  const [isTypePreorder, setIsTypePreorder] = useState<boolean>(false)
  const [isTypeShared, setIsTypeShared] = useState<boolean>(false)
  const [isTypeDelivery, setIsTypeDelivery] = useState<boolean>(false)
  const [dailySchedules, setDailySchedules] = useState<ScheduleType[]>([])
  const [preorderSchedules, setPreorderSchedules] = useState<ScheduleType[]>([])
  const [breadWidth, setBreadWidth] = useState<number>(0)
  const [breadDepth, setBreadDepth] = useState<number>(0)
  const [breadHeight, setBreadHeight] = useState<number>(0)

  const { storeDefaultBread, storeDefaultDeliveryBread, storeDefaultSchedules } = useFetchStoreDefault()

  // bread編集用関数
  const handlePrice = useNumberChangeEvent(setPrice)
  const handleBreadWidth = useNumberChangeEvent(setBreadWidth)
  const handleBreadDepth = useNumberChangeEvent(setBreadDepth)
  const handleBreadHeight = useNumberChangeEvent(setBreadHeight)
  // 当日取り置きの在庫を自動生成するかどうか
  const handleScheduledGeneration = useHandleScheduledGeneration(setScheduledGeneration, setDailySchedules)
  // schedules編集用関数
  const addSchedule = useAddSchedule(breadRef, setDailySchedules, setPreorderSchedules)
  const deleteSchedule = useDeleteSchedule(setDailySchedules, setPreorderSchedules)
  const addDefaultStock = useAddDefaultStock(setDailySchedules, setPreorderSchedules)
  const deleteDefaultStock = useDeleteDefaultStock(setDailySchedules, setPreorderSchedules)
  const handleDefaultStocks = useHandleDefaultStock(setDailySchedules, setPreorderSchedules)
  const handlePreorderHoursToDeadline = useHandleHoursToDeadline(setPreorderSchedules)
  const selectMaximumOrder = useSelectMaximumOrder(setDailySchedules, setPreorderSchedules)
  const updateNote = useUpdateNote(setDailySchedules, setPreorderSchedules)
  const selectDaysOfWeek = useSelectDaysOfWeek(setDailySchedules, setPreorderSchedules)
  const selectAllDaysOfWeek = useSelectAllDaysOfWeek(setDailySchedules, setPreorderSchedules)
  const unselectAllDaysOfWeek = useUnselectAllDaysOfWeek(setDailySchedules, setPreorderSchedules)

  // 注文タイプのチェックを外す際に受付設定を初期化する
  const initSchedulesByType = useInitSchedulesByType(
    breadRef,
    storeRef,
    getUpdateSchedulesFunction,
    setDailySchedules,
    setPreorderSchedules,
    setIsTypeDaily,
    setIsTypePreorder,
    setIsTypeShared,
    setIsTypeDelivery
  )

  // モーダルの開閉
  const openDeleteCautionModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '削除する',
        'キャンセル',
        '一度削除すると元に戻すことはできません。\n本当によろしいですか？',
        () => dispatch(deleteBread(breadRef)),
        '商品を削除します'
      )
    )
  }, [dispatch, breadRef])

  const openStatusChangeCautionModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '変更する',
        'キャンセル',
        '出品停止にすると、アプリ上では表示されなくなってしまいお客さまが通知をオンにできなくなりますがよろしいですか？',
        () => setStatus('private'),
        '出品ステータスを変更します'
      )
    )
  }, [dispatch, breadRef])

  const openDeleteScheduleCautionModal = useCallback(
    (modalType: StockUnionType) => {
      dispatch(
        openCautionModal(
          '変更する',
          'キャンセル',
          'すでに登録されている情報は削除されてしまいます。一時的な変更であれば、在庫を０にするや、受渡可能日を設定しないなどお試しください。',
          () => initSchedulesByType(modalType),
          '注文タイプを変更します'
        )
      )
    },
    [dispatch, initSchedulesByType]
  )

  // 販売停止を選択した時にモーダルを表示する
  const handleStatus = useHandleStatus(isEditPage, setStatus, openStatusChangeCautionModal)
  // 注文タイプのチェックを外す時にモーダルを表示する
  const toggleIsTypeDaily = useToggleTypeDaily(
    breadRef,
    storeRef,
    setIsTypeDaily,
    openDeleteScheduleCautionModal,
    setDailySchedules,
    setScheduledGeneration
  )
  const toggleIsTypePreorder = useToggleScheduleType(
    breadRef,
    storeRef,
    openDeleteScheduleCautionModal,
    setPreorderSchedules,
    setIsTypePreorder,
    'preorder'
  )
  const storeDeliveryBreadForm = useStoreDeliveryBread(
    breadRef,
    isTypeDelivery,
    storeRef,
    storeDeliveryBread,
    storeDefaultDeliveryBread,
    setIsTypeDelivery
  )

  // 当日取り置きの受渡曜日・受渡解禁時刻・オープン時在庫数に応じて、在庫が自動登録か都度配信かを判定する
  // 初回レンダリング時の判定はスキップする
  useEffect(() => {
    if (dailySchedules.length === 0) return
    const isScheduled = dailySchedules.some((dailySchedule) => {
      return (
        dailySchedule.days_of_week.length > 0 ||
        dailySchedule.default_stocks.some((defaultStock) => {
          return defaultStock.pickupable_time !== '' || defaultStock.stock > 0
        })
      )
    })
    setScheduledGeneration(isScheduled)
  }, [dailySchedules])

  // 新規登録の場合は初期値を、編集の場合は既存の値をセットする
  useEffect(() => {
    if (bread) {
      setAllergens([...bread.allergens])
      setBreadRef(bread.bread_ref)
      setName(bread.name)
      setShortName(bread.short_name || '')
      setImageUrls(bread.image_urls || [])
      setDescription(bread.description)
      setPrice(bread.price)
      setStatus(bread.status)
      setIsSetAllergens(bread.is_set_allergens)
      setIsTypeDaily(bread.is_type_daily)
      setIsTypePreorder(bread.is_type_preorder)
      setIsTypeShared(bread.is_type_shared || false)
      setIsTypeDelivery(bread.is_type_delivery || false)
    } else if (!bread && storeDefaultBread) {
      setAllergens([...storeDefaultBread.allergens])
      setCategoryId(storeDefaultBread.category.id)
      setDescription(storeDefaultBread.description)
      setPrice(storeDefaultBread.price)
      setIsSetAllergens(storeDefaultBread.is_set_allergens)
      setIsTypeDaily(storeDefaultBread.is_type_daily)
      setIsTypePreorder(storeDefaultBread.is_type_preorder)
      setIsTypeShared(storeDefaultBread.is_type_shared || false)
      setIsTypeDelivery(storeDefaultBread.is_type_delivery || false)
    } else {
      return
    }
  }, [bread, storeDefaultBread])

  // カテゴリの初期値を設定
  useEffect(() => {
    if (!categories || categories.length === 0) return
    if (!bread || !bread.category) {
      setCategoryId(categories[0].category_ref.id)
    } else {
      setCategoryId(bread.category.id)
    }
  }, [bread, categories])

  // 受付設定を取得する
  useEffect(() => {
    if (!storeRef || !breadRef || schedules.length > 0) return
    dispatch(fetchSchedulesOfBread(storeRef, breadRef))
  }, [storeRef, breadRef])

  // 取り寄せ設定を取得する
  useEffect(() => {
    // storeDeliveryBread.bread_refがundefinedでなければ、データ取得済み
    if (!storeRef || !breadRef || storeDeliveryBread.bread_ref) return
    dispatch(fetchStoreDeliveryBread(storeRef, breadRef))
  }, [storeRef, breadRef])

  // 当日以降の受渡可能日を取得する
  useEffect(() => {
    if (!storeRef || !breadRef || stocks.length > 0) return
    dispatch(fetchFutureStocks({ storeRef, types: ['preorder'], breadRef, isExcludingDeletedStock: false }))
  }, [storeRef, breadRef])

  // 受付設定に応じて値をセット
  useEffect(() => {
    if (schedules.length > 0) {
      setDailySchedules([...schedules.filter((schedule) => schedule.type === 'daily')])
      setPreorderSchedules([...schedules.filter((schedule) => schedule.type === 'preorder')])
      const schedule = schedules[0]
      if (!schedule && !storeDeliveryBread) return
      // 賞味期限を取得する
      const expirationDateOption =
        schedule.options.length === 0
          ? storeDeliveryBread.options.find((option) => option.key === '賞味期限')
          : schedule.options.find((option) => option.key === '賞味期限')
      setExpirationDate(expirationDateOption ? expirationDateOption.value : '')
      // 商品サイズを取得する
      const breadSizeOption = schedule
        ? schedule.options.find((option) => option.key === '商品サイズ')
        : storeDeliveryBread.options.find((option) => option.key === '商品サイズ')
      if (!breadSizeOption || breadSizeOption.value === '') return
      setBreadWidth(Number(breadSizeOption.value.split('x')[0]))
      setBreadDepth(Number(breadSizeOption.value.split('x')[1]))
      setBreadHeight(Number(breadSizeOption.value.split('x')[2]))
    } else if (schedules.length === 0 && storeDefaultSchedules.length > 0) {
      setDailySchedules([
        ...storeDefaultSchedules
          .filter((storeDefaultSchedule) => storeDefaultSchedule.type === 'daily')
          .map((storeDefaultSchedule) => {
            const { store_default_bread_ref, ...rest } = storeDefaultSchedule
            return {
              ...rest,
              bread_ref: breadRef,
              schedule_ref: storeRef.collection('store_schedules').doc(),
            }
          }),
      ])
      setPreorderSchedules([
        ...storeDefaultSchedules
          .filter((storeDefaultSchedule) => storeDefaultSchedule.type === 'preorder')
          .map((storeDefaultSchedule) => {
            const { store_default_bread_ref, ...rest } = storeDefaultSchedule
            return {
              ...rest,
              bread_ref: breadRef,
              schedule_ref: storeRef.collection('store_schedules').doc(),
            }
          }),
      ])
      const schedule = storeDefaultSchedules[0]
      if (!schedule && !storeDefaultDeliveryBread) return
      // 賞味期限を取得する
      const expirationDateOption =
        schedule.options.length === 0
          ? storeDefaultDeliveryBread && storeDefaultDeliveryBread.options.find((option) => option.key === '賞味期限')
          : schedule.options.find((option) => option.key === '賞味期限')
      setExpirationDate(expirationDateOption ? expirationDateOption.value : '')
    } else {
      return
    }
  }, [schedules, storeDeliveryBread, storeDefaultSchedules, storeDefaultDeliveryBread, breadRef, storeRef])

  // カテゴリー一覧を取得する
  useEffect(() => {
    if (categories.length > 0) return
    dispatch(fetchEnabledCategories())
  }, [])

  // 当日以降の事前予約の在庫設定が存在する場合、商品の削除はできない
  const isDeletable = stocks.length === 0

  // 必須項目を全て入力し、当日取り置きか事前予約の受付設定が完了したら確認画面へのボタンを活性化
  const isFilled = useFilledRequiredForm({
    name,
    imageUrls,
    description: parseDescriptionToHtml(),
    categoryId,
    price,
    status,
    isScheduledGeneration,
    isTypeDaily,
    dailySchedules,
    isTypePreorder,
    preorderSchedules,
    isTypeDelivery,
    storeDeliveryBreadForm,
  })

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'出品する商品の編集'} />
      <Spacer size={20} />
      <ProductEditBaseSection
        breadId={breadRef.id}
        storeId={storeRef.id}
        categories={categories}
        categoryId={categoryId}
        currentDescriptionLength={currentDescriptionLength}
        deleteImageUrls={deleteImageUrls}
        description={description}
        handleImageUrls={handleImageUrls}
        handleName={handleName}
        handleDescription={handleDescription}
        handlePrice={handlePrice}
        handleShortName={handleShortName}
        imageUrls={imageUrls}
        name={name}
        price={price}
        rearrangeImages={rearrangeImages}
        replaceImage={replaceImage}
        selectCategoryName={selectCategoryName}
        shortName={shortName}
      />
      <Spacer size={28} />
      <ProductEditAllergensSection
        allergens={allergens}
        handleAllergens={handleAllergens}
        handleSetAllergens={handleSetAllergens}
        isSetAllergens={isSetAllergens}
      />
      <Spacer size={28} />
      <FormLabel id={'order-type'} label={'注文タイプ'} required={true} />
      <Spacer size={8} />
      <SubDescription
        description={
          '当日取り置きできる商品と事前に予約できる商品として、注文できます。取り扱い方法にチェックを入れてください。'
        }
      />
      <Spacer size={8} />
      <ProductEditDailySchedule
        addDefaultStock={addDefaultStock}
        addSchedule={addSchedule}
        deleteDefaultStock={deleteDefaultStock}
        deleteSchedule={deleteSchedule}
        handleDefaultStocks={handleDefaultStocks}
        handleScheduledGeneration={handleScheduledGeneration}
        isChecked={isTypeDaily}
        isScheduledGeneration={isScheduledGeneration}
        schedules={dailySchedules}
        selectDaysOfWeek={selectDaysOfWeek}
        selectMaximumOrder={selectMaximumOrder}
        selectAllDaysOfWeek={selectAllDaysOfWeek}
        setDailySchedules={setDailySchedules}
        unselectAllDaysOfWeek={unselectAllDaysOfWeek}
        toggleCheckbox={toggleIsTypeDaily}
        type={'daily'}
        updateNote={updateNote}
      />
      <Spacer size={8} />
      <ProductEditPreorderSchedule
        addDefaultStock={addDefaultStock}
        addSchedule={addSchedule}
        deleteDefaultStock={deleteDefaultStock}
        deleteSchedule={deleteSchedule}
        handleDefaultStocks={handleDefaultStocks}
        handleHoursToDeadline={handlePreorderHoursToDeadline}
        label={'事前予約'}
        isChecked={isTypePreorder}
        schedules={preorderSchedules}
        selectDaysOfWeek={selectDaysOfWeek}
        selectMaximumOrder={selectMaximumOrder}
        selectAllDaysOfWeek={selectAllDaysOfWeek}
        setPreorderSchedules={setPreorderSchedules}
        unselectAllDaysOfWeek={unselectAllDaysOfWeek}
        toggleCheckbox={toggleIsTypePreorder}
        type={'preorder'}
        updateNote={updateNote}
      />
      <Spacer size={8} />
      <ProductEditDelivery isChecked={isTypeDelivery} label={'取り寄せ'} {...storeDeliveryBreadForm} />
      <Spacer size={28} />
      {isAdmin && (
        <>
          <FormLabel id={'status'} label={'商品紹介タグ'} required={true} />
          <Spacer size={8} />
          <FlexRow justifyContent={'start'} wrap={'wrap'}>
            {labels.map((label) => {
              const id = label.ref?.id
              if (!id) return
              return (
                <TagCheckbox
                  key={id}
                  id={id}
                  isChecked={breadLabels.some((breadLabel) => breadLabel.label_ref.id === id)}
                  onChange={handleBreadLabels}
                  type={'outlined'}
                  value={label.name}
                />
              )
            })}
          </FlexRow>
          <Spacer size={28} />
        </>
      )}
      <FormLabel id={'status'} label={'出品ステータス'} required={true} />
      <Spacer size={4} />
      <Caution caution={'廃版になるなど、長期間販売しなくなる時に出品停止にしてください。'} />
      <Spacer size={4} />
      <SubDescription
        description={'出品停止にすると、アプリ上では表示されなくなってしまいお客さまが通知をオンにできなくなります。'}
      />
      <Spacer size={8} />
      <RadioButtonsForm currentStatus={status} options={statuses} handleStatus={handleStatus} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        label={'賞味期限'}
        onChange={handleExpirationDate}
        required={false}
        size={'small'}
        type={'text'}
        value={expirationDate}
      />
      <Spacer size={28} />
      <FormLabel label={'商品サイズ(mm)'} required={false} />
      <Spacer size={8} />
      <ProductSizeForm
        breadWidth={breadWidth}
        breadDepth={breadDepth}
        breadHeight={breadHeight}
        handleBreadWidth={handleBreadWidth}
        handleBreadDepth={handleBreadDepth}
        handleBreadHeight={handleBreadHeight}
      />
      <Spacer size={48} />
      {isEditPage && ( // 編集ページでは削除ボタンを表示
        <>
          <NavButton
            disabled={!isDeletable}
            label={'商品を削除する'}
            iconPath={'/icon/grey/delete.svg'}
            onClick={openDeleteCautionModal}
            url={''}
          />
          {!isDeletable && (
            <>
              <Spacer size={8} />
              <Caution caution={'※この商品は、事前予約が設定中のため削除ができません。'} />
            </>
          )}
          <Spacer size={48} />
        </>
      )}
      <BlueButton
        disabled={!isFilled}
        endIconPath={'/icon/white/arrow_next_small.svg'}
        label={'確認画面へ'}
        onClick={async () => {
          const category = categories.find((category) => category.category_ref.id === categoryId)
          if (!category) return
          const now = FirebaseTimestamp.now()
          // 受付設定のオプションをまとめる
          const options: OptionType[] = []
          if (expirationDate !== '') {
            options.push({ key: '賞味期限', value: expirationDate })
          }
          if (breadWidth !== 0 && breadDepth !== 0 && breadHeight !== 0) {
            options.push({ key: '商品サイズ', value: `${breadWidth}x${breadDepth}x${breadHeight}` })
          }
          dispatch(confirmSchedules(dailySchedules, preorderSchedules, isTypeDaily, isTypePreorder, options))
          // 取り寄せ設定を保存
          if (isTypeDelivery) {
            const storeDeliveryBread: StoreDeliveryBreadType = {
              bread_ref: breadRef,
              maximum_order: storeDeliveryBreadForm.maximumOrder,
              package_size: storeDeliveryBreadForm.packageSize as PackageSize,
              delivery_company: storeDeliveryBreadForm.deliveryCompany,
              frozen_delivery_type: storeDeliveryBreadForm.frozenDeliveryType,
              is_frozen_delivery: storeDeliveryBreadForm.isFrozenDelivery,
              hours_to_deadline: storeDeliveryBreadForm.hoursToDeadline,
              how_to_eat: storeDeliveryBreadForm.howToEat,
              how_to_store: storeDeliveryBreadForm.howToStore,
              choices: storeDeliveryBreadForm.choices,
              options,
            }
            dispatch(confirmStoreDeliveryBread(storeDeliveryBread))
          }
          // 商品紹介タグを更新
          dispatch(confirmBreadLabels(breadLabels))
          const breadShopRef = shopRef || bread?.shop_ref
          dispatch(
            confirmBread({
              allergens: allergens,
              bread_ref: breadRef,
              category: category.category_ref,
              created_at: bread ? bread.updated_at : now,
              description: parseDescriptionToHtml(),
              image_url: imageUrls[0] || '', // TODO functionsの修正が終わったら削除
              image_urls: imageUrls,
              name: name,
              is_set_allergens: isSetAllergens,
              is_type_daily: isTypeDaily,
              is_type_preorder: isTypePreorder,
              is_type_shared: isTypeShared,
              is_type_delivery: isTypeDelivery,
              order: bread ? bread.order : 999,
              price: price,
              shop_ref: breadShopRef,
              short_name: shortName,
              status: status,
              updated_at: now,
            })
          )
          if (isEditPage) {
            dispatch(push(`/product/edit/confirm/${breadRef.id}`))
          } else {
            dispatch(push(`/product/confirm`))
          }
        }}
        size={'medium'}
        type={'main'}
      />
      <ProductEditError
        name={name}
        imageUrls={imageUrls}
        description={parseDescriptionToHtml()}
        categoryId={categoryId}
        price={price}
        status={status}
        isScheduledGeneration={isScheduledGeneration}
        isTypeDaily={isTypeDaily}
        dailySchedules={dailySchedules}
        isTypePreorder={isTypePreorder}
        preorderSchedules={preorderSchedules}
        isTypeDelivery={isTypeDelivery}
        storeDeliveryBreadForm={storeDeliveryBreadForm}
      />
      <Spacer size={72} />
    </div>
  )
}

export default ProductEditTemplate
